import React, { useMemo, useCallback, useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';

import { I18nContext } from '@bingads-webui-react/i18n-context';
import { AdFormatOption, AdFormat } from '@bingads-webui-campaign-react/display-plus-native-ads';
import { useWizardService, matchStepState, STEP_STATES } from '@bingads-webui-react/wizard';
import { DisplayAdsWithCopilot } from '@bingads-webui-campaign-react/xandr-display-ads-campaign-shared-components';

import { NativeAd } from '../native-ads/components/native-ad';
import { VideoAds } from '../video-ads/';
import { useSelectedAdFormatState } from '../../common/use-selected-ad-format';
import { NativeAdFooter } from '../native-ads/components/native-ad-footer';

export const MultiAdFormatStepShell = ({
  data,
  error,
  onDataChange,
  adsType = '',
  loadClipchampPlayer = null,
}) => {
  const [selectedAdFormat, setSelectedAdFormat] = useSelectedAdFormatState();
  const i18n = useContext(I18nContext);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const { state } = useWizardService();
  const currentStates = state.toStrings()[1];

  const onFormatChange = useCallback((value) => {
    setSelectedAdFormat(value);
    // Reset data
    onDataChange({ data: value === AdFormat.Native ? { data: {} } : { data: [] } });
  }, [onDataChange, setSelectedAdFormat]);

  const stepData = useMemo(() => {
    if (selectedAdFormat === AdFormat.Native) {
      return data || { data: {} };
    }

    return data || { data: [] };
  }, [data, selectedAdFormat]);

  // Wait for data is loaded to have a stable data state
  useEffect(() => {
    if (!isLoaded && matchStepState(state, STEP_STATES.ACTIVE)) {
      setIsLoaded(true);
    }
  }, [state, currentStates, isLoaded]);

  // To ensure disabled is fixed even we remove the ad when update
  useEffect(() => {
    if (isLoaded && !isDisabled) {
      const adData = _.isArray(stepData.data) ? stepData.data[0] : stepData.data;
      const adId = adData && adData.Id;

      if (adId) {
        setIsDisabled(true);
      }
    }
  }, [isLoaded, isDisabled, stepData.data]);

  // To ensure that ad format is Display when adsType is Display on initial load
  useEffect(() => {
    if (adsType === AdFormat.Display && selectedAdFormat !== AdFormat.Display) {
      setSelectedAdFormat(AdFormat.Display);
    }
  }, [adsType, setSelectedAdFormat]);

  return (
    <div className="multi-ad-format-step-shell">
      {isLoaded && (
        <>
          <AdFormatOption i18n={i18n} value={selectedAdFormat} onChange={onFormatChange} disabled={isDisabled} />
          {selectedAdFormat === AdFormat.Native && <NativeAd data={stepData} error={error} onDataChange={onDataChange} loadClipchampPlayer={loadClipchampPlayer} hideFooter supportImageFitting />}
          {selectedAdFormat === AdFormat.Display && <DisplayAdsWithCopilot data={stepData} error={error} onDataChange={onDataChange} hideFooter />}
          {selectedAdFormat === AdFormat.Video && <VideoAds data={stepData} error={error} onDataChange={onDataChange} loadClipchampPlayer={loadClipchampPlayer} hideFooter />}
          <NativeAdFooter />
        </>
      )}
    </div>
  );
};

MultiAdFormatStepShell.propTypes = {
  data: PropTypes.shape({}).isRequired,
  onDataChange: PropTypes.func.isRequired,
  error: PropTypes.shape({}).isRequired,
  adsType: PropTypes.string,
};

export const SingleMultiAdFormatStepShell = props => <MultiAdFormatStepShell {...props} isMultipleNative={false} />;
